import { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { deleteDoc, doc, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useBookings } from "./hooks/useBookings";
import { STATUS, STATUS_LABELS, EVENT_TYPE_LABELS } from "./schema";
import BookingCard from "./BookingCard";
import MorningBriefing from "./MorningBriefing";
import { STAFF_THEME, FONT } from "../../theme/staff";

const DAY_MS = 24 * 60 * 60 * 1000;
const AUTO_ARCHIVE_DAYS = 90;

// BookingsList uses `bg` / `line` keys rather than dark/raised — map them here
// without duplicating the rest of the shared palette.
const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised };

function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  if (typeof ts === "string") return new Date(ts);
  if (typeof ts === "number") return new Date(ts);
  return null;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfMonth(d) {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isAutoArchived(b, today) {
  if (b.status !== STATUS.COMPLETED && b.status !== STATUS.DECLINED) return false;
  // Anchor on confirmedDate if completed, else status change / submission.
  const anchor = toDate(b.confirmedDate) || toDate(b.statusChangedAt) || toDate(b.submittedAt);
  if (!anchor) return false;
  const daysOld = (today - anchor) / DAY_MS;
  return daysOld > AUTO_ARCHIVE_DAYS;
}

function isArchived(b, today) {
  return Boolean(b.archivedAt) || isAutoArchived(b, today);
}

function matchesSearch(b, q) {
  if (!q) return true;
  const hay = [
    b.name, b.company, b.email, b.phone,
    b.eventName, b.venueName, b.location,
    b.clientNotes, b.internalNotes,
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
}

function partition(bookings, { search, statusFilter, eventTypeFilter, showArchived, showTestData }) {
  const today = startOfDay(new Date());
  const upcoming = [];
  const tbd = [];
  const past = [];
  const archived = [];

  for (const b of bookings) {
    if (!showTestData && b._isTestData) continue;
    if (!matchesSearch(b, search)) continue;
    if (statusFilter !== "all" && b.status !== statusFilter) continue;
    if (eventTypeFilter !== "all" && b.eventType !== eventTypeFilter) continue;

    if (isArchived(b, today)) {
      archived.push(b);
      continue;
    }

    const confirmed = toDate(b.confirmedDate);
    if (confirmed && confirmed >= today) {
      upcoming.push(b);
    } else if (confirmed && confirmed < today) {
      past.push(b);
    } else if ([STATUS.NEW_INQUIRY, STATUS.CONTACTED, STATUS.QUOTED, STATUS.HOLD].includes(b.status)) {
      tbd.push(b);
    } else {
      past.push(b);
    }
  }

  upcoming.sort((a, b) => (toDate(a.confirmedDate) || 0) - (toDate(b.confirmedDate) || 0));
  past.sort((a, b) => (toDate(b.confirmedDate) || toDate(b.submittedAt) || 0) -
                     (toDate(a.confirmedDate) || toDate(a.submittedAt) || 0));
  tbd.sort((a, b) => (toDate(b.submittedAt) || 0) - (toDate(a.submittedAt) || 0));
  archived.sort((a, b) => (toDate(b.confirmedDate) || toDate(b.submittedAt) || 0) -
                         (toDate(a.confirmedDate) || toDate(a.submittedAt) || 0));

  return { upcoming, tbd, past, archived };
}

function computeStats(bookings) {
  const today = startOfDay(new Date());
  const monthStart = startOfMonth(today);
  let newInquiries = 0, quoted = 0, booked = 0, revenueMonth = 0;

  for (const b of bookings) {
    if (b.archivedAt || b.status === STATUS.SPAM) continue;
    if (b.status === STATUS.NEW_INQUIRY) newInquiries++;
    if (b.status === STATUS.QUOTED) quoted++;
    const confirmed = toDate(b.confirmedDate);
    if (confirmed && confirmed >= today &&
        [STATUS.BOOKED, STATUS.DEPOSIT_RECEIVED].includes(b.status)) {
      booked++;
    }
    if (confirmed && confirmed >= monthStart && confirmed < new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1)) {
      const rate = parseFloat(b.totalRate) || 0;
      revenueMonth += rate;
    }
  }
  return { newInquiries, quoted, booked, revenueMonth };
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      flex: 1,
      background: BRAND.panel,
      border: `1px solid ${BRAND.line}`,
      borderRadius: "10px",
      padding: "0.7rem 0.8rem",
      minWidth: 0,
    }}>
      <div style={{ fontSize: "0.7rem", color: BRAND.muted, fontWeight: 600, letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: "1.5rem", fontWeight: 800, color, marginTop: "0.2rem" }}>{value}</div>
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? BRAND.gold : "transparent",
        color: active ? BRAND.bg : BRAND.cream,
        border: `1px solid ${active ? BRAND.gold : BRAND.line}`,
        borderRadius: "999px",
        padding: "0.35rem 0.8rem",
        fontSize: "0.78rem",
        fontWeight: 700,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function Section({ title, items, onOpen, defaultOpen = true, accent = BRAND.gold, cardProps = {} }) {
  const [open, setOpen] = useState(defaultOpen);
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: "1.2rem" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent",
          border: "none",
          color: accent,
          fontWeight: 700,
          fontSize: "0.9rem",
          padding: "0.4rem 0",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          width: "100%",
          textAlign: "left",
          letterSpacing: "0.04em",
        }}
      >
        <span>{open ? "▼" : "▶"}</span>
        <span>{title.toUpperCase()}</span>
        <span style={{ color: BRAND.muted, fontWeight: 500 }}>· {items.length}</span>
      </button>
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.5rem" }}>
          {items.map((b) => (
            <BookingCard
              key={b.id}
              booking={b}
              onOpen={onOpen}
              onArchiveToggle={() => cardProps.onArchive?.(b)}
              onDelete={() => cardProps.onDelete?.(b)}
              selectionMode={cardProps.selectionMode}
              selected={cardProps.selectedIds?.has(b.id)}
              onToggleSelect={cardProps.onToggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Series row: collapsed view of N recurring bookings ──

function SeriesRow({ seriesName, bookings, onOpen, cardProps }) {
  const [expanded, setExpanded] = useState(false);
  const next = bookings[0]; // already sorted by date asc
  const nextDate = toDate(next.confirmedDate);
  const fmtNext = nextDate
    ? nextDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    : "TBD";
  const statusColor = {
    booked: BRAND.blue,
    deposit_received: BRAND.orange,
    completed: BRAND.muted,
  }[next.status] || BRAND.gold;

  return (
    <div style={{ marginBottom: "0.5rem" }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          gap: "0.75rem",
          alignItems: "center",
          width: "100%",
          background: expanded ? "#1a3068" : BRAND.panel,
          border: `1px solid ${expanded ? BRAND.gold : BRAND.line}`,
          borderRadius: expanded ? "10px 10px 0 0" : "10px",
          padding: "0.75rem 1rem",
          cursor: "pointer",
          textAlign: "left",
          color: BRAND.text,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "0.95rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ color: BRAND.gold, fontSize: "0.8rem" }}>🔁</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{seriesName}</span>
          </div>
          <div style={{ fontSize: "0.78rem", color: BRAND.muted, marginTop: "0.15rem" }}>
            Next: <span style={{ color: BRAND.gold, fontWeight: 600 }}>{fmtNext}</span>
            {next.venueName ? ` · ${next.venueName}` : ""}
          </div>
        </div>
        <span style={{
          background: "rgba(252,179,31,0.15)",
          color: BRAND.gold,
          borderRadius: "999px",
          padding: "0.2rem 0.65rem",
          fontSize: "0.75rem",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}>
          {bookings.length} dates
        </span>
        <span style={{ color: BRAND.muted, fontSize: "0.85rem" }}>{expanded ? "▼" : "▶"}</span>
      </button>
      {expanded && (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          padding: "0.5rem",
          background: "rgba(20,37,72,0.5)",
          border: `1px solid ${BRAND.gold}`,
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
        }}>
          {bookings.map((b) => (
            <BookingCard
              key={b.id}
              booking={b}
              onOpen={onOpen}
              onArchiveToggle={() => cardProps.onArchive?.(b)}
              onDelete={() => cardProps.onDelete?.(b)}
              selectionMode={cardProps.selectionMode}
              selected={cardProps.selectedIds?.has(b.id)}
              onToggleSelect={cardProps.onToggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Upcoming section: month groups + series collapsing ──

function fmtMonthYear(d) {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function UpcomingSection({ items, onOpen, cardProps }) {
  const [collapsedMonths, setCollapsedMonths] = useState({});
  if (items.length === 0) return null;

  // Step 1: Pull out series (2+ bookings sharing eventName or seriesName) across ALL upcoming
  const seriesMap = {};
  const standalone = [];
  for (const b of items) {
    const sid = (b.seriesName || b.eventName || "").trim();
    if (sid) {
      if (!seriesMap[sid]) seriesMap[sid] = [];
      seriesMap[sid].push(b);
    } else {
      standalone.push(b);
    }
  }

  // Only treat as a series if 2+ total upcoming — otherwise it's just a one-off
  const seriesGroups = [];
  for (const [name, bookings] of Object.entries(seriesMap)) {
    if (bookings.length >= 2) {
      seriesGroups.push({ name, bookings });
    } else {
      standalone.push(...bookings);
    }
  }

  // Step 2: Group standalone by month
  const months = [];
  const monthMap = {};
  standalone.sort((a, b) => (toDate(a.confirmedDate) || 0) - (toDate(b.confirmedDate) || 0));
  for (const b of standalone) {
    const d = toDate(b.confirmedDate);
    const key = d ? `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}` : "tbd";
    if (!monthMap[key]) {
      const entry = { key, label: d ? fmtMonthYear(d) : "Date TBD", items: [] };
      monthMap[key] = entry;
      months.push(entry);
    }
    monthMap[key].items.push(b);
  }

  const toggleMonth = (key) => setCollapsedMonths((prev) => ({ ...prev, [key]: !prev[key] }));
  const totalCount = items.length;

  return (
    <div style={{ marginBottom: "1.2rem" }}>
      <div style={{
        color: BRAND.gold,
        fontWeight: 700,
        fontSize: "0.9rem",
        padding: "0.4rem 0",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        letterSpacing: "0.04em",
      }}>
        <span>UPCOMING</span>
        <span style={{ color: BRAND.muted, fontWeight: 500 }}>· {totalCount}</span>
      </div>

      {/* Series groups first — collapsed across all months */}
      {seriesGroups.map(({ name, bookings }) => (
        <SeriesRow key={`series-${name}`} seriesName={name} bookings={bookings} onOpen={onOpen} cardProps={cardProps} />
      ))}

      {/* Then month-grouped standalone bookings */}
      {months.map((month, i) => {
        // Default: first 2 months open, rest collapsed
        const effectiveOpen = collapsedMonths[month.key] === undefined ? i < 2 : !collapsedMonths[month.key];
        return (
          <div key={month.key} style={{ marginBottom: "0.75rem" }}>
            <button
              type="button"
              onClick={() => toggleMonth(month.key)}
              style={{
                background: "transparent",
                border: "none",
                color: BRAND.cream,
                fontWeight: 600,
                fontSize: "0.82rem",
                padding: "0.35rem 0",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                width: "100%",
                textAlign: "left",
              }}
            >
              <span style={{ fontSize: "0.75rem" }}>{effectiveOpen ? "▼" : "▶"}</span>
              <span>{month.label}</span>
              <span style={{ color: BRAND.muted, fontWeight: 500, fontSize: "0.78rem" }}>· {month.items.length}</span>
            </button>
            {effectiveOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.35rem" }}>
                {month.items.map((b) => (
                  <BookingCard
                    key={b.id}
                    booking={b}
                    onOpen={onOpen}
                    onArchiveToggle={() => cardProps.onArchive?.(b)}
                    onDelete={() => cardProps.onDelete?.(b)}
                    selectionMode={cardProps.selectionMode}
                    selected={cardProps.selectedIds?.has(b.id)}
                    onToggleSelect={cardProps.onToggleSelect}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function readUrlFilters() {
  const p = new URLSearchParams(window.location.search);
  return {
    search: p.get("q") || "",
    statusFilter: p.get("status") || "all",
    eventTypeFilter: p.get("type") || "all",
    showArchived: p.get("archived") === "1",
    showTestData: p.get("test") === "1",
  };
}

function writeUrlFilters(filters) {
  const p = new URLSearchParams();
  if (filters.search) p.set("q", filters.search);
  if (filters.statusFilter !== "all") p.set("status", filters.statusFilter);
  if (filters.eventTypeFilter !== "all") p.set("type", filters.eventTypeFilter);
  if (filters.showArchived) p.set("archived", "1");
  if (filters.showTestData) p.set("test", "1");
  const qs = p.toString();
  const url = window.location.pathname + (qs ? `?${qs}` : "");
  window.history.replaceState(null, "", url);
}

export default function BookingsList({ navigate }) {
  const { bookings, loading, error } = useBookings();
  const initial = useMemo(() => readUrlFilters(), []);
  const [search, setSearch] = useState(initial.search);
  const [statusFilter, setStatusFilter] = useState(initial.statusFilter);
  const [eventTypeFilter, setEventTypeFilter] = useState(initial.eventTypeFilter);
  const [showArchived, setShowArchived] = useState(initial.showArchived);
  const [showTestData, setShowTestData] = useState(initial.showTestData);

  // Sync filters to URL so browser back restores them
  useEffect(() => {
    writeUrlFilters({ search, statusFilter, eventTypeFilter, showArchived, showTestData });
  }, [search, statusFilter, eventTypeFilter, showArchived, showTestData]);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelected(new Set());
  };

  const archiveOne = async (booking) => {
    const next = booking.archivedAt ? null : Timestamp.now();
    const verb = booking.archivedAt ? "Unarchive" : "Archive";
    if (!window.confirm(`${verb} "${booking.eventName || booking.name || "this booking"}"?`)) return;
    try {
      await setDoc(doc(db, "bookings", booking.id), { archivedAt: next, updatedAt: serverTimestamp() }, { merge: true });
    } catch (err) {
      alert(`${verb} failed: ${err.message}`);
    }
  };

  const deleteOne = async (booking) => {
    const label = booking.eventName || booking.name || "this booking";
    if (!window.confirm(`Permanently delete "${label}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, "bookings", booking.id));
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  const bulkArchive = async () => {
    if (selected.size === 0 || bulkBusy) return;
    if (!window.confirm(`Archive ${selected.size} booking${selected.size === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    const stamp = Timestamp.now();
    try {
      await Promise.all(Array.from(selected).map((id) =>
        setDoc(doc(db, "bookings", id), { archivedAt: stamp, updatedAt: serverTimestamp() }, { merge: true })
      ));
      exitSelectionMode();
    } catch (err) {
      alert(`Bulk archive failed: ${err.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0 || bulkBusy) return;
    if (!window.confirm(`Permanently delete ${selected.size} booking${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      await Promise.all(Array.from(selected).map((id) => deleteDoc(doc(db, "bookings", id))));
      exitSelectionMode();
    } catch (err) {
      alert(`Bulk delete failed: ${err.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const partitioned = useMemo(
    () => partition(bookings, { search, statusFilter, eventTypeFilter, showArchived, showTestData }),
    [bookings, search, statusFilter, eventTypeFilter, showArchived, showTestData]
  );
  const stats = useMemo(() => computeStats(bookings), [bookings]);

  const openBooking = (id, tab) => navigate(`/crm/bookings/${id}${tab ? `?tab=${tab}` : ""}`);

  return (
    <div style={{
      background: BRAND.bg,
      minHeight: "100vh",
      color: BRAND.text,
      fontFamily: FONT.body,
      padding: "1rem",
      boxSizing: "border-box",
    }}>
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>

        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}>
          <h1 style={{ margin: 0, fontSize: "1.4rem", color: BRAND.gold, flex: 1 }}>📇 Bookings</h1>
          <button
            type="button"
            onClick={() => navigate("/crm/bookings/new")}
            style={{
              background: BRAND.gold,
              color: BRAND.dark,
              border: "none",
              borderRadius: "8px",
              padding: "0.5rem 0.9rem",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "0.85rem",
            }}
          >
            + New Booking
          </button>
          <button
            type="button"
            onClick={() => navigate("/crm/add")}
            style={{
              background: "transparent",
              color: BRAND.cream,
              border: `1px solid ${BRAND.line}`,
              borderRadius: "8px",
              padding: "0.5rem 0.9rem",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.85rem",
            }}
          >
            ⚡ Paste &amp; Parse
          </button>
          <button
            type="button"
            onClick={() => navigate("/crm/add-public-event")}
            title="Quick-add a public event without client/financial fields"
            style={{
              background: "transparent",
              color: BRAND.cream,
              border: `1px solid ${BRAND.line}`,
              borderRadius: "8px",
              padding: "0.5rem 0.9rem",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.85rem",
            }}
          >
            🌐 Public Event
          </button>
          {/* 🧪 Test data button moved to Admin → Test Harness to keep the
              CRM primary toolbar free of dev affordances. */}
          <button
            type="button"
            onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
            style={{
              background: selectionMode ? BRAND.gold : "transparent",
              color: selectionMode ? BRAND.bg : BRAND.cream,
              border: `1px solid ${selectionMode ? BRAND.gold : BRAND.line}`,
              borderRadius: "8px",
              padding: "0.5rem 0.85rem",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "0.82rem",
            }}
          >
            {selectionMode ? "✓ Done" : "☑ Select"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/menu")}
            style={{
              background: "transparent",
              color: BRAND.muted,
              border: `1px solid ${BRAND.border}`,
              borderRadius: "8px",
              padding: "0.5rem 0.85rem",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.82rem",
            }}
          >
            ← Menu
          </button>
        </div>

        {error && (
          <div style={{
            background: "#6b2234", color: "white", padding: "0.7rem 1rem",
            borderRadius: "8px", marginBottom: "1rem", fontSize: "0.85rem",
          }}>
            Error loading bookings: {error.message}
          </div>
        )}

        <MorningBriefing bookings={bookings} onOpen={openBooking} />

        {/* Stats */}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          <StatCard label="New Inquiries" value={stats.newInquiries} color={BRAND.sky} />
          <StatCard label="Quoted" value={stats.quoted} color={BRAND.gold} />
          <StatCard label="Booked Upcoming" value={stats.booked} color={BRAND.blue} />
          <StatCard label="This Month" value={`$${stats.revenueMonth.toLocaleString("en-US", { maximumFractionDigits: 0 })}`} color={BRAND.orange} />
        </div>

        {/* Search + filters */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
          <input
            type="text"
            placeholder="Search name, email, venue, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              background: BRAND.panel,
              border: `1px solid ${BRAND.line}`,
              borderRadius: "8px",
              color: BRAND.text,
              padding: "0.6rem 0.8rem",
              fontSize: "0.9rem",
              outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
            <FilterPill active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>All</FilterPill>
            {["new_inquiry", "contacted", "quoted", "booked", "deposit_received", "completed", "hold"].map((s) => (
              <FilterPill key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>
                {STATUS_LABELS[s]}
              </FilterPill>
            ))}
          </div>
          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", alignItems: "center" }}>
            <select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              style={{
                background: BRAND.panel,
                border: `1px solid ${BRAND.line}`,
                borderRadius: "8px",
                color: BRAND.text,
                padding: "0.45rem 0.6rem",
                fontSize: "0.85rem",
                outline: "none",
              }}
            >
              <option value="all">All event types</option>
              {Object.entries(EVENT_TYPE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: BRAND.muted, fontSize: "0.82rem" }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: showTestData ? "#fcb31f" : BRAND.muted, fontSize: "0.82rem", fontWeight: showTestData ? 600 : 400 }}>
              <input
                type="checkbox"
                checked={showTestData}
                onChange={(e) => setShowTestData(e.target.checked)}
              />
              🧪 Show test data
            </label>
          </div>
        </div>

        {loading && <div style={{ color: BRAND.muted, padding: "2rem", textAlign: "center" }}>Loading…</div>}

        {!loading && bookings.length === 0 && !error && (
          <div style={{
            background: BRAND.panel,
            border: `1px dashed ${BRAND.line}`,
            borderRadius: "10px",
            padding: "2rem",
            textAlign: "center",
            color: BRAND.muted,
          }}>
            No bookings yet. New form submissions will land here.
          </div>
        )}

        {!loading && (() => {
          const cardProps = {
            onArchive: archiveOne,
            onDelete: deleteOne,
            selectionMode,
            selectedIds: selected,
            onToggleSelect: toggleSelect,
          };
          return (
            <>
              <UpcomingSection items={partitioned.upcoming} onOpen={openBooking} cardProps={cardProps} />
              <Section title="Date TBD" items={partitioned.tbd} onOpen={openBooking} accent={BRAND.sky} defaultOpen cardProps={cardProps} />
              <Section title="Past" items={partitioned.past} onOpen={openBooking} accent={BRAND.muted} defaultOpen={false} cardProps={cardProps} />
              {showArchived && (
                <Section title="Archived" items={partitioned.archived} onOpen={openBooking} accent={BRAND.muted} defaultOpen={false} cardProps={cardProps} />
              )}
            </>
          );
        })()}
      </div>

      {/* Bulk action bar — appears when selection mode is on and at least one selected */}
      {selectionMode && selected.size > 0 && (
        <div style={{
          position: "fixed",
          bottom: "1rem", left: "50%", transform: "translateX(-50%)",
          background: BRAND.panel,
          border: `1px solid ${BRAND.gold}`,
          borderRadius: "999px",
          padding: "0.55rem 0.85rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          zIndex: 30,
          flexWrap: "wrap",
          maxWidth: "calc(100vw - 2rem)",
          justifyContent: "center",
        }}>
          <span style={{ color: BRAND.gold, fontWeight: 800, fontSize: "0.88rem" }}>
            {selected.size} selected
          </span>
          <button
            type="button"
            onClick={bulkArchive}
            disabled={bulkBusy}
            style={{
              background: BRAND.blue, color: "white", border: "none",
              borderRadius: "999px", padding: "0.45rem 0.95rem",
              fontWeight: 700, fontSize: "0.82rem",
              cursor: bulkBusy ? "default" : "pointer",
            }}
          >
            📦 Archive
          </button>
          <button
            type="button"
            onClick={bulkDelete}
            disabled={bulkBusy}
            style={{
              background: "#c94444", color: "white", border: "none",
              borderRadius: "999px", padding: "0.45rem 0.95rem",
              fontWeight: 700, fontSize: "0.82rem",
              cursor: bulkBusy ? "default" : "pointer",
            }}
          >
            🗑 Delete
          </button>
          <button
            type="button"
            onClick={exitSelectionMode}
            disabled={bulkBusy}
            style={{
              background: "transparent", color: BRAND.cream,
              border: `1px solid ${BRAND.line}`, borderRadius: "999px",
              padding: "0.45rem 0.85rem", fontWeight: 600, fontSize: "0.8rem",
              cursor: bulkBusy ? "default" : "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
